import { create } from 'zustand';
import type { ObjectId } from '@/types';

/**
 * Window-manager session state: which window is active and the order in which
 * windows were focused. Not persisted and not undoable — it is view state, so it
 * lives apart from the document. The document owns the durable parts (frame, state).
 */
export interface WindowStore {
  readonly activeWindowId: ObjectId | null;
  /** Most recently focused last. */
  readonly focusHistory: readonly ObjectId[];
  readonly launcherOpen: boolean;
  readonly focus: (id: ObjectId) => void;
  readonly blur: () => void;
  /** Drops a window (closed or missing) and activates the previously focused one. */
  readonly forget: (id: ObjectId) => void;
  readonly prune: (existing: ReadonlySet<ObjectId>) => void;
  readonly setLauncherOpen: (open: boolean) => void;
}

const MAX_HISTORY = 50;

export const useWindowStore = create<WindowStore>()((set, get) => ({
  activeWindowId: null,
  focusHistory: [],
  launcherOpen: false,
  focus: (id) => {
    if (get().activeWindowId === id) return;
    set((s) => ({
      activeWindowId: id,
      focusHistory: [...s.focusHistory.filter((x) => x !== id), id].slice(-MAX_HISTORY),
    }));
  },
  blur: () => {
    if (get().activeWindowId !== null) set({ activeWindowId: null });
  },
  forget: (id) => {
    set((s) => {
      if (!s.focusHistory.includes(id) && s.activeWindowId !== id) return s;
      const focusHistory = s.focusHistory.filter((x) => x !== id);
      return {
        focusHistory,
        activeWindowId: s.activeWindowId === id ? (focusHistory[focusHistory.length - 1] ?? null) : s.activeWindowId,
      };
    });
  },
  prune: (existing) => {
    set((s) => {
      const focusHistory = s.focusHistory.filter((x) => existing.has(x));
      const activeWindowId =
        s.activeWindowId && existing.has(s.activeWindowId)
          ? s.activeWindowId
          : (focusHistory[focusHistory.length - 1] ?? null);
      return focusHistory.length === s.focusHistory.length && activeWindowId === s.activeWindowId
        ? s
        : { focusHistory, activeWindowId };
    });
  },
  setLauncherOpen: (open) => {
    if (get().launcherOpen !== open) set({ launcherOpen: open });
  },
}));
